import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  startAt,
  endAt,
  limit,
} from "firebase/firestore";
import "./AdminCommissionBonus.css";

const AdminCommissionBonus = () => {
  const [searchUsername, setSearchUsername] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState(null);
  const [bonusSummary, setBonusSummary] = useState(null);
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [betsSummary, setBetsSummary] = useState(null); // 🔹 odds
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // 🔹 Fetch username suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchUsername.trim().length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const usersRef = collection(db, "users");
        const q = query(
          usersRef,
          orderBy("username"),
          startAt(searchUsername),
          endAt(searchUsername + "\uf8ff"),
          limit(5)
        );

        const snap = await getDocs(q);
        const results = snap.docs.map((doc) => doc.data().username);
        setSuggestions(results);
      } catch (err) {
        console.error("Suggestion error:", err);
      }
    };

    fetchSuggestions();
  }, [searchUsername]);

  // 🔹 Handle search
  const handleSearch = async (usernameFromSuggestion) => {
    const username = usernameFromSuggestion || searchUsername.trim();
    if (!username) {
      setMessage("⚠️ Tafadhali weka username kwanza.");
      return;
    }

    setSearchUsername(username);
    setLoading(true);
    setMessage("");
    setCommissionSummary(null);
    setBonusSummary(null);
    setTransactionSummary(null);
    setBetsSummary(null);
    setTransactions([]);
    setSuggestions([]);

    try {
      await Promise.all([
        fetchCommission(username),
        fetchBonus(username),
        fetchTransactions(username),
        fetchUserBets(username), // 🔹 fetch odds
      ]);
    } catch (err) {
      console.error(err);
      setMessage("❌ Hitilafu imejitokeza wakati wa kupata data.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Fetch commission
  const fetchCommission = async (username) => {
    const commissionsRef = collection(db, "commissions");
    const q = query(commissionsRef, where("username", "==", username));
    const snapshot = await getDocs(q);

    let total = 0;
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (data.status !== "success") return;
      const amount = Number(data.amount) || 0;
      total += amount;
    });

    setCommissionSummary({ total });
  };

  // 🔹 Fetch bonus
  const fetchBonus = async (username) => {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("username", "==", username));
    const userSnap = await getDocs(q);
    if (userSnap.empty) {
      setBonusSummary({ total: 0 });
      return;
    }

    const userDoc = userSnap.docs[0];
    const bonusHistoryRef = collection(userDoc.ref, "bonusHistory");
    const bonusSnap = await getDocs(bonusHistoryRef);

    let total = 0;
    bonusSnap.docs.forEach((doc) => {
      const data = doc.data();
      const amount = Number(data.amount) || 0;
      total += amount;
    });

    setBonusSummary({ total });
  };

  // 🔹 Fetch all historical success transactions (TSH & USDT)
  const fetchTransactions = async (username) => {
    try {
      const tshQuery = query(
        collection(db, "tsh_transactions"),
        where("username", "==", username),
        where("status", "==", "success")
      );
      const usdtQuery = query(
        collection(db, "usdt_transactions"),
        where("username", "==", username),
        where("status", "==", "success")
      );

      const [tshSnap, usdtSnap] = await Promise.all([
        getDocs(tshQuery),
        getDocs(usdtQuery),
      ]);

      const combined = [
        ...tshSnap.docs.map((doc) => ({ id: doc.id, method: "TSH", ...doc.data() })),
        ...usdtSnap.docs.map((doc) => ({ id: doc.id, method: "USDT", ...doc.data() })),
      ];

      combined.sort((a, b) => {
        const aTime = a.timestamp?.seconds || 0;
        const bTime = b.timestamp?.seconds || 0;
        return bTime - aTime;
      });

      let total = combined.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);

      setTransactions(combined);
      setTransactionSummary({ total });
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  // 🔹 Fetch user bets to sum odds
  const fetchUserBets = async (username) => {
    try {
      const betsQuery = query(collection(db, "userBets"), where("username", "==", username));
      const betsSnap = await getDocs(betsQuery);

      const bets = betsSnap.docs.map(doc => doc.data());

      const totalOdds = bets.reduce((sum, bet) => sum + (Number(bet.odds) || 0), 0);

      setBetsSummary({ totalOdds });
    } catch (err) {
      console.error("Error fetching user bets:", err);
      setBetsSummary({ totalOdds: 0 });
    }
  };

  const formatStatus = (status) => {
    if (status === "processing") return "Processing";
    if (status === "success") return "Success";
    if (status === "rejected") return "Rejected";
    return status;
  };

  return (
    <div className="admin-commission-bonus">
      <h2>🔎 Admin — Commission, Bonus, Transaction & Odds Checker</h2>

      <div className="search-box">
        <input
          type="text"
          placeholder="Tafuta username..."
          value={searchUsername}
          onChange={(e) => setSearchUsername(e.target.value)}
        />
        <button onClick={() => handleSearch()}>Search</button>
        {suggestions.length > 0 && (
          <ul className="suggestion-list">
            {suggestions.map((s, idx) => (
              <li key={idx} onClick={() => handleSearch(s)}>
                {s}
              </li>
            ))}
          </ul>
        )}
      </div>

      {loading && <p>⏳ Inapakia...</p>}
      {message && <p className="message">{message}</p>}

      {commissionSummary && (
        <div className="summary-section">
          <h3>💰 Commission Summary</h3>
          <ul>
            <li><b>Total:</b> {commissionSummary.total.toLocaleString()} TSH</li>
          </ul>
        </div>
      )}

      {bonusSummary && (
        <div className="summary-section">
          <h3>🎁 Bonus Summary</h3>
          <ul>
            <li><b>Total:</b> {bonusSummary.total.toLocaleString()} TSH</li>
          </ul>
        </div>
      )}

      {transactionSummary && (
        <div className="summary-section">
          <h3>📊 Total Success Transactions</h3>
          <ul>
            <li><b>Total:</b> {transactionSummary.total.toLocaleString()} TSH/USDT</li>
          </ul>
        </div>
      )}

      {betsSummary && (
        <div className="summary-section">
          <h3>🎯 Total Odds</h3>
          <ul>
            <li><b>Odds Zote:</b> {betsSummary.totalOdds.toFixed(2)}</li>
          </ul>
        </div>
      )}

      {transactions.length > 0 && (
        <div className="transactions-table-container">
          <h3>📝 Transactions Detail</h3>
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Method</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id}>
                  <td>{tx.timestamp?.seconds ? new Date(tx.timestamp.seconds * 1000).toLocaleString() : "-"}</td>
                  <td>{tx.method}</td>
                  <td>{Number(tx.amount).toLocaleString()}</td>
                  <td>{formatStatus(tx.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCommissionBonus;